import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DialogService } from 'src/app/shared/service/dialog.service';
import {
  AnnouncementForm,
  INSTALLATION_NECESSITY,
  INSTALLATION_TYPES,
  TechnicalInstallation,
} from '../../model/technical-installations.model';
import { extendTextArea } from 'src/app/shared/style';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatSuffix, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ValidateButtonComponent } from '../../../shared/buttons/validate-button.component';
import { SecondaryButtonComponent } from '../../../shared/buttons/secondary-button.component';
import { RequiredIndicatorPipe } from '../../../shared/pipes/required-indicator.pipe';

@Component({
  selector: 'sibat-announcement-form-dialog',
  templateUrl: './announcement-form-dialog.component.html',
  styleUrls: ['../form-dialog.style.scss'],
  animations: [
    trigger('openClose', [
      state(
        'closed',
        style({
          display: 'none',
        })
      ),
      transition('closed => open', [
        animate('.4s', keyframes([style({ opacity: 0, display: 'block', offset: 0 }), style({ opacity: 1, offset: 1 })])),
      ]),
    ]),
  ],
  imports: [
    TranslocoDirective,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    CdkTextareaAutosize,
    MatDialogActions,
    ValidateButtonComponent,
    SecondaryButtonComponent,
    RequiredIndicatorPipe,
    TranslocoPipe,
  ],
})
export class AnnouncementFormDialogComponent implements AfterViewInit, OnDestroy {
  installationTypes = Object.values(INSTALLATION_TYPES);
  necessities = Object.values(INSTALLATION_NECESSITY);
  announcementForm: UntypedFormGroup;

  readonly editionMode: boolean = false;

  private defaultInstallationType = INSTALLATION_TYPES.fireDetection;
  private installationTypeSubscription?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<AnnouncementFormDialogComponent, AnnouncementForm>,
    private store: Store,
    @Inject(MAT_DIALOG_DATA) private data: { defaultValue?: TechnicalInstallation; buildingId?: number },
    private dialogService: DialogService
  ) {
    const defaultValue = this.data.defaultValue;
    this.editionMode = !!defaultValue;
    this.announcementForm = new UntypedFormGroup({
      installationType: new UntypedFormControl(defaultValue?.installationType ?? this.defaultInstallationType, [Validators.required]),
      designation: new UntypedFormControl(defaultValue?.designation),
      installationDate: new UntypedFormControl(defaultValue?.installationDate, [Validators.required]),
      lastControlDate: new UntypedFormControl(defaultValue?.lastControlDate ?? null),
      necessity: new UntypedFormControl(defaultValue?.necessity, [Validators.required]),
      location: new UntypedFormControl(defaultValue?.location),
      remarks: new UntypedFormControl(defaultValue?.remarks),
    });
    if (this.editionMode) {
      this.announcementForm.get('installationType')?.disable();
      this.announcementForm.get('installationDate')?.disable();
    }

    this.announcementForm.get('lastControlDate')?.statusChanges.subscribe(() => {
      const control = this.announcementForm.get('lastControlDate');
      if (control?.errors?.['matDatepickerParse']) {
        // Remet la valeur à null pour éviter la chaîne vide "" qui casse la validation
        control.setValue(null, { emitEvent: false });
        // Puis on supprime l'erreur pour que le contrôle soit valide
        control.setErrors(null);
      }
    });
  }
  ngAfterViewInit() {
    setTimeout(() => {
      const textareas = document.querySelectorAll(
        'textarea[formControlName="remarks"], ' + 'textarea[formControlName="location"]'
      ) as NodeListOf<HTMLTextAreaElement>;
      textareas.forEach(textarea => this.extendTextArea(textarea));
    }, 0);
  }

  extendTextArea(element) {
    extendTextArea(element);
  }

  ngOnDestroy(): void {
    this.installationTypeSubscription?.unsubscribe();
  }

  async validate() {
    const formValue = this.announcementForm.getRawValue();

    const transformedDates = this.transformDates(formValue);

    const resultForm: AnnouncementForm = this.editionMode
      ? {
          ...(this.data.defaultValue ?? {}),
          ...formValue,
          ...transformedDates,
        }
      : {
          ...formValue,
          ...transformedDates,
          status: 'ACTIVE',
          createdAt: new Date().toISOString(),
        };

    this.dialogRef.close(resultForm);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private formatDateTimeLocal(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}:${s}`;
  }

  private transformDates(formValue: any): Partial<AnnouncementForm> {
    const transformed: Partial<AnnouncementForm> = {};

    if (formValue.installationDate instanceof Date) {
      transformed.installationDate = this.formatDateTimeLocal(formValue.installationDate);
    }

    if (formValue.lastControlDate instanceof Date) {
      transformed.lastControlDate = this.formatDateTimeLocal(formValue.lastControlDate);
    }

    return transformed;
  }
}
